import { useMemo } from "react";
import { FileIcon, ChevronDown, EyeOff } from "lucide-react";
import { FileIconComponent } from "../../components/file-icon/file-icon.component";
import { getFileTypeWithDocumentType } from "@/lib/utils/file-utils";
import { cn } from "../../lib/utils";
import { FileEntityTypeWithUrl } from "../../hooks/useStorage";
import { Loading } from "@/components/src/animations/loading";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../../components/ui/collapsible";

interface VendorJobFilesProps {
	files: FileEntityTypeWithUrl[];
	loading?: boolean;
	selectedFileId: string | null;
	onSelectFile: (id: string) => void;
	sharedUntil?: Record<string, string | null>;
}

const FILE_GROUPS = [
	{ key: "ls", label: "Lieferscheine", types: ["ls"] as const },
	{ key: "kv", label: "Kostenvoranschläge", types: ["kv"] as const },
	{
		key: "docs",
		label: "Weitere Dokumente",
		types: ["pdf", "xml", "txt", "gs"] as const,
	},
	{ key: "media", label: "Fotos & STLs", types: ["img", "3d"] as const },
] as const;

export const VendorJobFiles: React.FC<VendorJobFilesProps> = ({
	files,
	loading,
	selectedFileId,
	onSelectFile,
	sharedUntil = {},
}) => {
	const isUnshared = (fileId: string) => {
		return sharedUntil[fileId] != null;
	};

	const groupedFiles = useMemo(() => {
		const groups = FILE_GROUPS.map((g) => ({
			...g,
			files: [] as FileEntityTypeWithUrl[],
		}));

		files.forEach((file) => {
			const type = getFileTypeWithDocumentType(
				file.meta_data as Record<string, any> | null,
				file.file_name
			);
			const group =
				groups.find((g) =>
					(g.types as readonly string[]).includes(type as string)
				) || groups.find((g) => g.key === "docs");
			group?.files.push(file);
		});

		groups.forEach((g) => {
			g.files.sort(
				(a, b) =>
					new Date(b.created_at || 0).getTime() -
					new Date(a.created_at || 0).getTime()
			);
		});

		return groups.filter((g) => g.files.length > 0);
	}, [files]);

	if (loading) {
		return (
			<div className="rounded-lg border border-stone-200 p-4">
				<div className="flex items-center gap-2 mb-3">
					<FileIcon size={16} className="text-stone-400" />
					<h3 className="font-medium text-stone-800">
						Dateien vom Labor
					</h3>
				</div>
				<Loading />
			</div>
		);
	}

	if (files.length === 0) {
		return (
			<div className="rounded-lg border border-stone-200 p-4">
				<div className="flex items-center gap-2 mb-3">
					<FileIcon size={16} className="text-stone-400" />
					<h3 className="font-medium text-stone-800">
						Dateien vom Labor
					</h3>
				</div>
				<p className="text-sm text-stone-500">Keine Dateien geteilt</p>
			</div>
		);
	}

	return (
		<div className="rounded-lg border border-stone-200 overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-3 border-b border-stone-200">
				<FileIcon size={16} className="text-stone-400" />
				<h3 className="font-medium text-stone-800">
					Dateien vom Labor ({files.length})
				</h3>
			</div>

			<div className="divide-y divide-stone-100">
				{groupedFiles.map((group) => (
					<Collapsible key={group.key} defaultOpen>
						<CollapsibleTrigger className="flex items-center justify-between w-full px-4 py-2 text-left hover:bg-stone-50 transition-colors">
							<span className="text-sm font-medium text-stone-600">
								{group.label} ({group.files.length})
							</span>
							<ChevronDown
								size={16}
								className="text-stone-400 transition-transform [[data-state=open]>&]:rotate-180"
							/>
						</CollapsibleTrigger>
						<CollapsibleContent>
							<div className="pb-1">
								{group.files.map((file) => {
									const fileType =
										getFileTypeWithDocumentType(
											file.meta_data as Record<
												string,
												any
											> | null,
											file.file_name
										);
									const unshared = isUnshared(file.id);
									return (
										<button
											key={file.id}
											onClick={() =>
												onSelectFile(file.id)
											}
											className={cn(
												"w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-stone-50 transition-colors",
												file.id === selectedFileId &&
													"bg-stone-100",
												unshared && "opacity-50"
											)}
											aria-label={`Datei ${file.file_name} auswählen`}
											data-testid={`vendor-job-file-${file.id}`}
										>
											{unshared ? (
												<EyeOff
													size={16}
													className="text-stone-400 flex-shrink-0"
												/>
											) : (
												<FileIconComponent
													fileType={fileType}
													size={16}
												/>
											)}
											<span
												className={cn(
													"truncate text-sm",
													unshared
														? "text-stone-400 line-through"
														: "text-stone-700"
												)}
												title={
													file.file_name || undefined
												}
											>
												{file.file_name}
											</span>
										</button>
									);
								})}
							</div>
						</CollapsibleContent>
					</Collapsible>
				))}
			</div>
		</div>
	);
};
